import { tt } from '@agos/shared'

/**
 * The scratch card's and click-and-win's prize slots, as a form edits them - `FRONTEND.md` §9.481.
 *
 * **Why there was no editor.** `campaignFields` drops any setting whose value is an object, which is right
 * for a generic scalar editor and means `slots` - an array - was invisible with nothing saying so. Legacy
 * has a full editor for these, so the owner could tune their prize payouts on the old system and not on this
 * one. `BACKEND.md` §10A.241 is the rule; this is the control that respects it.
 *
 * **It mirrors `WheelForm` on purpose.** §9.160 and §9.478 built this exact shape for the spin wheel - rows
 * edited as strings, one problem kind per rule, each message naming the row. The field names genuinely
 * differ (`probability` against `weight`, `none` against `no_win`) so the code is not shared, but the shape
 * is, because two different editors for two weighted prize lists is how a rule gets fixed on one and left
 * broken on the other.
 */

/** What a slot may pay in. `none` is legacy's *Skús znova*, and the reason a value of 0 is legal. */
export const SLOT_REWARDS = ['points', 'coupon', 'coupon_percent', 'none'] as const

export type SlotReward = (typeof SLOT_REWARDS)[number]

/** A percentage, and the sum of them may not pass this. */
const FULL_CHANCE = 100

export interface SlotRow {
  label: string
  reward: SlotReward
  /** Money or points as typed; a string because that is what an input holds. */
  value: string
  /** A percentage as typed. Blank is refused; `0` is a slot deliberately parked. */
  probability: string
}

export interface SlotForm {
  rows: SlotRow[]
}

/**
 * The reward type, accepting both spellings the owner's data carries.
 *
 * **`CampaignService.slotRewardType`'s rule, ported rather than guessed**: `MiniGameService`'s own comment
 * reads *"Two spellings of the same field in the wild: the editor writes rewardType, older rows carry type.
 * Whichever is there wins; neither means 'no prize'."* An editor reading only one of the two would blank the
 * type on every legacy row it opened, and then save that blank back.
 */
function rewardOf(slot: Record<string, unknown>): SlotReward {
  /* **A string or nothing, and lint is why.** `String(unknown)` on an object answers `"[object Object]"`,
     which would then fail the membership test below and read as `none` - the right answer by accident. A
     stored type that is not a string is not a reward type, so it is refused on its own terms. */
  const raw = slot.type ?? slot.rewardType
  const named = typeof raw === 'string' ? raw : 'none'
  return (SLOT_REWARDS as readonly string[]).includes(named) ? (named as SlotReward) : 'none'
}

/**
 * A stored scalar as an input holds it.
 *
 * **Only a primitive, which lint caught and which matters.** `String(unknown)` on an object answers
 * `"[object Object]"` - so a malformed row whose `value` is a map would have put that text into the box and
 * then, on the next save, onto the wire. An empty box is the honest reading of a value this editor cannot
 * represent, and `slotProblems` then refuses the row for having no value rather than sending nonsense.
 */
function text(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/** The stored slots as a form fills them. An absent or malformed list reads as no rows. */
export function slotFormOf(settings: unknown): SlotForm {
  if (typeof settings !== 'object' || settings === null) return { rows: [] }
  const stored = (settings as { slots?: unknown }).slots
  if (!Array.isArray(stored)) return { rows: [] }
  const rows = stored.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const slot = entry as Record<string, unknown>
    return [
      {
        label: text(slot.label),
        reward: rewardOf(slot),
        value: text(slot.value),
        probability: text(slot.probability),
      },
    ]
  })
  return { rows }
}

/** A row the add button creates, matching legacy's own new-slot defaults. */
export function newSlot(): SlotRow {
  return { label: tt('Nové políčko'), reward: 'points', value: '10', probability: '0' }
}

export type SlotProblem =
  | { kind: 'no-slots' }
  | { kind: 'empty-label' }
  /** A winning slot worth nothing or less - §10A.240's rule, third surface. */
  | { kind: 'worthless-slot'; label: string }
  /** A *Skús znova* carrying a value, which would be a lie whichever way it resolved. */
  | { kind: 'loser-with-value'; label: string }
  /** A percentage discount over a hundred. */
  | { kind: 'over-full-discount'; label: string }
  /** Blank, not a number, or outside 0 to 100. */
  | { kind: 'bad-probability'; label: string }
  /** The sum past a hundred, which puts later slots out of reach. */
  | { kind: 'over-full-chance'; percent: number }

/** A typed number, or `null` when the text is not one. Blank is not a number. */
function number(raw: string): number | null {
  if (raw.trim() === '') return null
  const parsed = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * What stops the slots being saved - every rule here is `BACKEND.md` §10A.241's.
 *
 * **The probability's emptiness is its own condition, and that is the server's own late finding.**
 * `Number('')` is 0 and **0 is a legal probability** - a slot the owner has parked - so no range test can
 * express *nothing was typed*. On the server the same distinction needed an `isNumeric` because `number`
 * there answers 0 for an unparseable string too.
 *
 * **A sum under a hundred is not a problem**, which is where this and legacy's editor differ: the remainder
 * is the chance of winning nothing, `draw`'s javadoc says so, and AGOS's own seeded defaults sum to 30 and
 * 23. `slotShareNote` states it instead of refusing it.
 */
export function slotProblems(form: SlotForm): SlotProblem[] {
  const problems: SlotProblem[] = []
  if (form.rows.length === 0) problems.push({ kind: 'no-slots' })
  if (form.rows.some((row) => row.label.trim() === '')) problems.push({ kind: 'empty-label' })

  let chance = 0
  for (const row of form.rows) {
    const label = row.label.trim()
    const worth = number(row.value)
    if (row.reward === 'none') {
      if (worth !== null && worth !== 0) problems.push({ kind: 'loser-with-value', label })
    } else if (worth === null || worth <= 0) {
      problems.push({ kind: 'worthless-slot', label })
    } else if (row.reward === 'coupon_percent' && worth > FULL_CHANCE) {
      problems.push({ kind: 'over-full-discount', label })
    }

    const odds = number(row.probability)
    if (odds === null || odds < 0 || odds > FULL_CHANCE) {
      problems.push({ kind: 'bad-probability', label })
    } else {
      chance += odds
    }
  }
  if (chance > FULL_CHANCE) {
    problems.push({ kind: 'over-full-chance', percent: Math.round(chance) })
  }
  return problems
}

export function slotProblemMessage(problem: SlotProblem): string {
  if (problem.kind === 'no-slots') return tt('Hra potrebuje aspoň jedno políčko.')
  if (problem.kind === 'empty-label') {
    return tt('Každé políčko potrebuje popis - zákazník ho uvidí na karte.')
  }
  if (problem.kind === 'over-full-chance') {
    /* Names the figure, because *the sum is too high* on a game of a dozen slots does not say by how much
       and the operator has to choose which one to lower. */
    return `${tt('Súčet pravdepodobností je')} ${problem.percent} %, ${tt('čo je viac než 100 - na políčka za hranicou sa nedá trafiť.')}`
  }
  /* Each of the rest names the slot, because a game has up to a dozen and *a value is wrong* would send the
     operator through all of them. The label is what they see in the editor and what the customer sees on the
     card, so it is the one identifier both ends share - §9.478's reasoning on the wheel. */
  if (problem.kind === 'worthless-slot') {
    return `${problem.label}: ${tt('zadajte hodnotu vyššiu než nula - inak políčko sľúbi výhru a nedá nič.')}`
  }
  if (problem.kind === 'loser-with-value') {
    return `${problem.label}: ${tt('políčko bez výhry nemá mať hodnotu.')}`
  }
  if (problem.kind === 'over-full-discount') {
    return `${problem.label}: ${tt('zľava nemôže byť vyššia než 100 %.')}`
  }
  return `${problem.label}: ${tt('pravdepodobnosť musí byť číslo od 0 do 100.')}`
}

/**
 * How the chance is divided, said rather than refused.
 *
 * Legacy's indicator reads *Súčet: N % ✓* or *(musí byť 100%)*. This states both halves, because the
 * remainder is a real outcome on this server: what is left over is the chance of winning nothing.
 */
export function slotShareNote(form: SlotForm): string {
  const chance = form.rows.reduce((sum, row) => sum + (number(row.probability) ?? 0), 0)
  const shared = Math.round(chance)
  if (shared > FULL_CHANCE) {
    return `${tt('Rozdelené')} ${shared} % - ${tt('viac než 100')}`
  }
  return `${tt('Rozdelené')} ${shared} %, ${tt('bez výhry')} ${FULL_CHANCE - shared} %`
}

/**
 * The slots as `PATCH /campaigns/{type}` takes them.
 *
 * **Written as `rewardType`**, which is what the catalogue uses and what `MiniGameService` falls back to;
 * `type` is read as well but writing one spelling keeps the stored rows consistent from here on.
 *
 * **Numbers, not the strings the form holds.** The server's guard accepts either, but a stored `"10"` would
 * read back into the form identically and then be written again, so the jsonb would slowly fill with
 * quoted numbers - which is exactly the shape §10A.193's `pizza_sizes` defect was.
 */
export function slotsPayload(form: SlotForm): Array<Record<string, unknown>> {
  return form.rows.map((row) => ({
    label: row.label.trim(),
    rewardType: row.reward,
    value: number(row.value) ?? 0,
    probability: number(row.probability) ?? 0,
  }))
}

/** Whether the operator moved anything, so an untouched save writes no `slots` key at all. */
export function slotsChanged(original: unknown, form: SlotForm): boolean {
  const stored = slotFormOf(original)
  if (stored.rows.length !== form.rows.length) return true
  return stored.rows.some((row, index) => {
    const next = form.rows[index]
    if (next === undefined) return true
    return (
      row.label !== next.label ||
      row.reward !== next.reward ||
      row.value !== next.value ||
      row.probability !== next.probability
    )
  })
}

/** What each reward is called where a person reads it. */
export function slotRewardLabel(reward: SlotReward): string {
  if (reward === 'points') return tt('Body')
  if (reward === 'coupon') return tt('Kupón €')
  if (reward === 'coupon_percent') return tt('Kupón %')
  return tt('Skús znova')
}
